import { cn } from '../../lib/utils';

/**
 * Standardizovaný stavový indikátor — tečka nebo badge.
 *
 * status:
 *   running / online / active / enabled → zelená
 *   stopped / offline / inactive        → šedá
 *   building / starting / pending       → žlutá pulzující
 *   error / failed / dead / exited      → červená
 *
 * Použití:
 *   <StatusDot status="running" />
 *   <StatusDot status="offline" withLabel />
 *   <StatusDot status={container.state} className="flex-shrink-0" />
 */

export type ServiceStatus = string; // záměrně string — mapujeme libovolné hodnoty

const RUNNING = new Set(['running', 'online', 'active', 'enabled', 'up', 'started', 'healthy']);
const BUILDING = new Set(['building', 'starting', 'pending', 'restarting', 'deploying', 'loading']);
const ERROR = new Set(['error', 'failed', 'dead', 'exited', 'unhealthy', 'crash']);

function dotColor(status: string): string {
  const s = status?.toLowerCase() ?? '';
  if (RUNNING.has(s)) return 'bg-green-400';
  if (BUILDING.has(s)) return 'bg-yellow-400 animate-pulse';
  if (ERROR.has(s)) return 'bg-red-400';
  return 'bg-gray-500';
}

function labelColor(status: string): string {
  const s = status?.toLowerCase() ?? '';
  if (RUNNING.has(s)) return 'text-green-400';
  if (BUILDING.has(s)) return 'text-yellow-400';
  if (ERROR.has(s)) return 'text-red-400';
  return 'text-gray-500';
}

interface StatusDotProps {
  status: ServiceStatus;
  /** Zobrazit i text statusu vedle tečky */
  withLabel?: boolean;
  /** Custom label místo status stringu */
  label?: string;
  className?: string;
  size?: 'sm' | 'md';
}

export function StatusDot({ status, withLabel, label, className, size = 'sm' }: StatusDotProps) {
  const dot = (
    <span
      className={cn(
        'rounded-full flex-shrink-0',
        size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5',
        dotColor(status),
      )}
    />
  );

  if (!withLabel) return <span className={cn('inline-flex items-center', className)}>{dot}</span>;

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {dot}
      <span className={cn('text-xs font-medium', labelColor(status))}>
        {label ?? status}
      </span>
    </span>
  );
}
